import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {usePaginaHistorial, useRestaurarPagina} from '@/hooks/bateria/use-bateria-ciclo-vida'
import {translateApiError} from '@/lib/errors'
import {formatDateTime} from '@/lib/risk'

/** G29′ (adenda §7.2): cada reemplazo de página queda visible y deshacible, sin prohibir re-procesar
 * un cuadernillo. */
export function PaginaHistorialDialog({
  businessId,
  evaluacionId,
  paquete,
  open,
  onOpenChange,
}: {
  businessId: string
  evaluacionId: string
  paquete: BateriaPaquete
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const numeros = paquete.paginas_reemplazadas
  const [numero, setNumero] = useState<number | undefined>(numeros[0])
  const {data, isLoading} = usePaginaHistorial(businessId, paquete.id, numero, open)
  const restaurar = useRestaurarPagina(businessId, evaluacionId, paquete.id)
  const [error, setError] = useState<string | null>(null)

  function close() {
    setError(null)
    restaurar.reset()
    onOpenChange(false)
  }

  function onRestaurar(version: number) {
    if (numero === undefined) return
    setError(null)
    restaurar.mutate({numero, version}, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="pagina-historial-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.paginaHistorial.title', {nombre: paquete.employee_name})}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {numeros.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                {t('admin.bateria.paginaHistorial.pagina')}
              </span>
              {/* Botones y no un select: R3 es para listas de entidades, no para 2-3 números visibles. */}
              {numeros.map((n) => (
                <Button
                  key={n}
                  type="button"
                  size="xs"
                  variant={n === numero ? 'default' : 'outline'}
                  data-testid={`pagina-historial-numero-${n}`}
                  onClick={() => setNumero(n)}>
                  {n}
                </Button>
              ))}
            </div>
          )}
          {isLoading || !data ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ul className="space-y-2">
              <li
                className="surface flex items-center justify-between gap-3 rounded-md px-3 py-2"
                data-testid="pagina-historial-actual">
                <div>
                  <Tag tone="neutral">{t('admin.bateria.paginaHistorial.actual')}</Tag>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('admin.bateria.paginaHistorial.version', {n: data.actual_version})}
                  </p>
                </div>
                {data.actual_download_url && (
                  <a
                    href={data.actual_download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-primary hover:underline">
                    {t('admin.bateria.paginaHistorial.ver')}
                  </a>
                )}
              </li>
              {[...data.historial].reverse().map((entrada) => (
                <li
                  key={entrada.version}
                  className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
                  data-testid={`pagina-historial-version-${entrada.version}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">
                      {t('admin.bateria.paginaHistorial.version', {n: entrada.version})} ·{' '}
                      {t(`admin.bateria.paginaHistorial.origen.${entrada.origen}`, {defaultValue: entrada.origen})}
                    </p>
                    <p className="font-mono text-[11px] text-muted-foreground">{formatDateTime(entrada.replaced_at)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {entrada.download_url && (
                      <a
                        href={entrada.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-primary hover:underline">
                        {t('admin.bateria.paginaHistorial.ver')}
                      </a>
                    )}
                    <Button
                      size="xs"
                      variant="outline"
                      data-testid={`pagina-historial-restaurar-${entrada.version}`}
                      disabled={restaurar.isPending}
                      onClick={() => onRestaurar(entrada.version)}>
                      {t('admin.bateria.paginaHistorial.restaurar')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {restaurar.isSuccess && (
            <p className="text-xs text-primary" data-testid="pagina-historial-restaurado">
              {t('admin.bateria.paginaHistorial.restaurado')}
            </p>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={close}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
